import { useCallback, useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  SaleDepositEditRequestFilter,
  SaleDepositEditRequestStatusFilter,
} from "../types/sale-deposit-edit-request.type";
import EditRequestFilter from "../components/SaleDepositEditRequest/Filter";
import EditRequestTable from "../components/SaleDepositEditRequest/Table";
import { EDIT_REQUEST_WINDOW_DAYS } from "../components/SaleDepositEditRequest/editRequest.utils";
import SaleDepositReportTabs, {
  SALE_DEPOSIT_REPORT_TITLE,
} from "../components/SaleDepositReport/ReportTabs";
import { SALE_DEPOSIT_EDIT_REQUEST_PATH } from "../components/SaleDepositReport/depositReportUrl";
import { UNFILTERED_OPTION } from "../components/SaleDepositReport/useBranchRouteOptions";
import { useUserRole } from "../utils/hooks/useUserRole";
import { notifySaleDepositEditRequestDecided } from "../utils/hooks/useSaleDepositEditRequestCount";

const API_DATE_FORMAT = "YYYY-MM-DD";

/**
 * คำขอแก้ไขยอดโอน — where a branch's request to reopen one closed deposit is answered.
 *
 * The second tab of รายงานการโอน rather than a report of its own: it is an approval queue
 * over the very deposits that report lists, which is where the customer asked for it —
 * "ให้มีแถบย่อยของรายงานการโอน เป็นคำขอแก้ไขยอดโอน".
 *
 * Opens on the pending queue over the seven days a request may still be raised for: that is
 * the whole population of requests anyone can still do something about, and it means the
 * supervisor lands on their own inbox rather than on a filter row to fill in.
 */
export default function SaleDepositEditRequestPage() {
  const { isHeadOfficeAccounting, isMaster } = useUserRole();
  // Same rule as the รายงานการโอน filter: only a cross-branch reader picks a branch; everyone
  // else is scoped to their own by the token, so no branch is ever sent from here for them.
  const canSelectBranch = Boolean(isHeadOfficeAccounting || isMaster);

  const [status, setStatus] =
    useState<SaleDepositEditRequestStatusFilter>("pending");
  const [dateFrom, setDateFrom] = useState<Dayjs>(() =>
    dayjs().subtract(EDIT_REQUEST_WINDOW_DAYS, "day").startOf("day"),
  );
  const [dateTo, setDateTo] = useState<Dayjs>(() => dayjs().startOf("day"));
  const [selectedBranch, setSelectedBranch] =
    useState<string>(UNFILTERED_OPTION);
  const [selectedRoute, setSelectedRoute] = useState<string>(UNFILTERED_OPTION);
  const changeDateRange = useCallback((from: Dayjs, to: Dayjs) => {
    setDateFrom(from);
    setDateTo(to);
  }, []);

  const filter = useMemo<SaleDepositEditRequestFilter>(
    () => ({
      status,
      dateFrom: dateFrom.format(API_DATE_FORMAT),
      dateTo: dateTo.format(API_DATE_FORMAT),
      branchCode: canSelectBranch ? selectedBranch : undefined,
      routeCode: selectedRoute,
    }),
    [status, dateFrom, dateTo, canSelectBranch, selectedBranch, selectedRoute],
  );

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Stack mb={2}>
        <Typography variant="h6">{SALE_DEPOSIT_REPORT_TITLE}</Typography>
        <SaleDepositReportTabs current={SALE_DEPOSIT_EDIT_REQUEST_PATH} />
        <Typography variant="body2" color="text.secondary" mt={1}>
          คำขอเปิดแก้ไขรายการโอนที่ปิดศูนย์เซลไปแล้ว
          สาขาส่งคำขอได้ภายใน {EDIT_REQUEST_WINDOW_DAYS} วันนับจากวันที่ปิดศูนย์เซล
          และหัวหน้าบัญชีสำนักงานใหญ่เป็นผู้อนุมัติหรือปฏิเสธ
        </Typography>
      </Stack>

      <Paper sx={{ padding: "24px 16px 16px", mb: 2 }}>
        <EditRequestFilter
          status={status}
          onChangeStatus={setStatus}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onChangeDateRange={changeDateRange}
          canSelectBranch={canSelectBranch}
          selectedBranch={selectedBranch}
          onChangeBranch={setSelectedBranch}
          selectedRoute={selectedRoute}
          onChangeRoute={setSelectedRoute}
        />
      </Paper>

      {/* Every accepted decision is announced app-wide rather than kept on this page: the
          same pending count is on the sidebar, which is not inside this tree. */}
      <EditRequestTable
        filter={filter}
        onRequestDecided={notifySaleDepositEditRequestDecided}
      />
    </Box>
  );
}
